
import { useMemo, useState } from 'react';
import Page from '../../../../../components/PageWrapper';
import GTable from '../../../../../components/elements/GTable';
import useModal from '../../../../../hooks/modalHook';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ITablePaginationData } from '../../../../../components/elements/GTable/GTable';
import {
  capitalizeSentence,
  getVehicleProtectionFromKey,
  onRemoveFilter,
  removeUnderscoresAndCapitalize
} from '../../../../../utils/miscFunctions';
import useRoute from '../../../../../hooks/routesHook';
import useUserAccount from '../../../../../hooks/userAccountHook';
import { Claim } from '../../../../../models/claim/Claim';
import SelectDropdown, {
  ISelectDropdownItem
} from '../../../../../components/form-control/SelectDropdown';
import { CLAIM_STAGES } from '../../../../../resources/enums';
import moment from 'moment';
import ClaimStages from '../../../../../components/elements/ClaimStages';
import { useQuery } from '@tanstack/react-query';
import { TwoStateVerifiedCheck } from '../../../../../components/statuses';
import { useSelector } from 'react-redux';
import { getSelectedCountry, getEnumsFromState } from '../../../../../states/slices/resourceSlice';
import { useClaim } from '../../../../../hooks/claimHook';
import TableFilter from '../../../../../components/elements/GTable/InGTableComponents/TableFilter';
import { ITableFilterItem } from '../../../../../resources/interfaces';
import TableFilterForm from '../../../../../components/elements/GTable/InGTableComponents/TableFilter/TableFilterForm';
import Loader from '../../../../../components/Loader';
import PolicyStatus from '../../../../../components/elements/PolicyStatus';

import { IEnum } from '../../../../../resources/interfaces';
import { useTableFilters } from '../../../../../hooks/useTableFilters';

export const CLAIM_TABLE_FILTER_OPTIONS: ISelectDropdownItem[] = [
  {
    name: 'All',
    value: 'All'
  },
  {
    name: 'New',
    value: CLAIM_STAGES.NEW
  },
  {
    name: 'In Review',
    value: CLAIM_STAGES.IN_REVIEW
  },
  {
    name: 'Acknowledged',
    value: CLAIM_STAGES.ACKNOWLEDGED
  },
  {
    name: 'DV Issued',
    value: CLAIM_STAGES.DV_ISSUED
  },
  {
    name: 'DV Signed',
    value: CLAIM_STAGES.DV_SIGNED
  },
  {
    name: 'Adjusted',
    value: CLAIM_STAGES.ADJUSTED
  },
  {
    name: 'Assessed',
    value: CLAIM_STAGES.ASSESSED
  },
  {
    name: 'Paid',
    value: CLAIM_STAGES.PAID
  }
]
interface ClaimsContent {
  amount: number;
  claimNumber: string;
  claimStage: string;
  createdOn: Date;
  customerName: string;
  customerPhone: string;
  expiry: Date;
  ncdEffect: string;
  paid: boolean;
  policyInception: Date;
  policyNumber: string;
  protectionType: string;
  reviewStage: string;
  status: any;
  updatedOn: Date;
}

export interface ClaimsProps {
  content: ClaimsContent[];
  empty: boolean;
  first: boolean;
  last: true;
  number: number;
  numberOfElements: number;
  pageable: any;
  size: number;
  sort: any;
  totalElements: number;
  totalPages: number
}

const AutoClaims = (): JSX.Element => {
  const navigate = useNavigate();
  const { getDashboardBase } = useRoute();
  const { claimsPaginationData, getAllClaimsList, columns, getDefaultFilters } = useClaim();
  const [searchParams] = useSearchParams();
  const [searchString, setSearchString] = useState<string>('');
  const [showPerPage, setShowPerPage] = useState<number>(50);
  const { ModalComponent } = useModal();
  const [currentPage, setCurrentPage] = useState<number>(searchParams.get('page') ? Number(searchParams.get('page')) : 0);
  const [openFilter, setOpenFilter] = useState<boolean>(false);

  const { filters: tableFilters, setFilters, getFilterValue } = useTableFilters({
    initialFilters: getDefaultFilters(),
    syncWithUrl: true,
  });

  const reviewStage = getFilterValue('stage');



  const { data: claimsResponse, isLoading: claimsLoading } = useQuery({
    queryKey: ['getAllClaimsList', showPerPage, currentPage, reviewStage, searchString],
    queryFn: () => getAllClaimsList(currentPage, showPerPage, searchString, reviewStage)
  });


  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <Page title="Claims">
      <div className="bg-white dark:bg-darkBg w-full min-h-full p-5 rounded-lg shadow-md dark:shadow-darkBg-shadow">

        <div className="">
          <div className="inline-flex items-center gap-2 mb-2 dark:text-darkBg-mainText">
            <div className="h-8 w-2 bg-gradient-to-b from-yellow-500 to-yellow-700 rounded"></div>
            <h2 className="font-semibold text-3xl">Claims</h2>
          </div>

          <p className="text-sm text-slate-400">
            View a list of all claims and associated details.
          </p>
        </div>

        <div className="overflow-x-auto mt-4">
          <GTable
            columns={columns}
            data={claimsResponse ?? []}
            classes="h-fit"
            onSearch={(searchString: string) => setSearchString(searchString)}
            searchPlaceholder={'Search by customer name...'}
            tableType={'claims'}
            paginationData={claimsPaginationData}
            onPageChange={handlePageChange}
            showPerPage={showPerPage}
            loading={claimsLoading}
            onShowPerPageChange={(page: number) => setShowPerPage(page)}
            onRowClick={(claim: Claim) => {
              navigate(`${getDashboardBase()}/claims/${claim.claimNumber}?type=motor`, {
                replace: true
              });
            }}
            selectedFilters={tableFilters}
            onRemoveFilter={(filter: ITableFilterItem, index: number) => {
              onRemoveFilter(filter, index, tableFilters, setFilters)
            }}
            showFilter={
              <TableFilter
                openFilter={openFilter}
                onFilterChange={(filterState: boolean) => setOpenFilter(filterState)}
                content={
                  <TableFilterForm
                    filters={tableFilters}
                    getValues={(filters: ITableFilterItem[]) => {
                      setFilters(filters);
                      setOpenFilter(false);
                    }}
                    onResetFilters={() => setFilters(getDefaultFilters())}
                  />
                }
              />
            }
          />
        </div>
      </div>
      <ModalComponent />
    </Page>
  );
};

export default AutoClaims;
